// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import * as runtimeEnv from "../../utils/runtimeEnv";
import { ApiServiceError } from "./apiError";

/**
 * Design ruling on issue 910 (failed writes): "You're signed out. Sign in
 * again, then try again."
 */
export const SIGNED_OUT_MESSAGE = "You're signed out. Sign in again, then try again.";

/**
 * Thrown by an API client when it has no access token and demo data is off.
 * No request is sent and no fixture is returned (issue 910).
 */
export class AuthTokenUnavailableError extends Error {
  public readonly name = "AuthTokenUnavailableError";

  constructor() {
    super(SIGNED_OUT_MESSAGE);
  }
}

export interface DemoDataContext {
  token?: string | undefined;
  allowTestModeRequests?: boolean | undefined;
}

function isProductionBuild(): boolean {
  // Read safely, like the demo flag: a partial runtimeEnv mock means "not prod".
  const flag = (runtimeEnv as Partial<typeof runtimeEnv>).isProductionBuild;
  return typeof flag === "function" && flag();
}

/** isDemoDataEnabled(), read safely (see below). */
export function demoDataEnabled(): boolean {
  if (isProductionBuild()) {
    return false;
  }
  // Read through the module so a suite that swaps runtimeEnv for a partial
  // mock (without the flag) still means "demo data off".
  const flag = (runtimeEnv as Partial<typeof runtimeEnv>).isDemoDataEnabled;
  return typeof flag === "function" && flag();
}

type TokenUnavailableListener = () => void;
const tokenUnavailableListeners = new Set<TokenUnavailableListener>();

/** Subscribes to clients refusing a call for want of a token. */
export function onAuthTokenUnavailable(listener: TokenUnavailableListener): () => void {
  tokenUnavailableListeners.add(listener);
  return () => {
    tokenUnavailableListeners.delete(listener);
  };
}

type TokenAvailableListener = () => void;
const tokenAvailableListeners = new Set<TokenAvailableListener>();

export function onAuthTokenAvailable(listener: TokenAvailableListener): () => void {
  tokenAvailableListeners.add(listener);
  return () => {
    tokenAvailableListeners.delete(listener);
  };
}

export function notifyAuthTokenAvailable(): void {
  tokenAvailableListeners.forEach((listener) => listener());
}

/**
 * The caller's token, or AuthTokenUnavailableError (announced like the shared
 * check) when there is none. For clients that must send a token once
 * shouldUseDemoData has said "go to the server", e.g. allowTestModeRequests.
 */
export function requireToken(context?: DemoDataContext): string {
  if (typeof context?.token !== "string" || context.token.length === 0) {
    tokenUnavailableListeners.forEach((listener) => listener());
    throw new AuthTokenUnavailableError();
  }
  return context.token;
}

/**
 * The one demo-data decision for every API client (issue 910):
 * - a production build never serves fixtures: the build-time PROD flag is
 *   checked first, so neither a runtime MODE=test (siteconfig.json) nor
 *   DEMO_DATA can turn them on;
 * - `allowTestModeRequests` always goes to the server;
 * - test mode or an explicit demo flag (`isDemoDataEnabled`) uses fixtures;
 * - otherwise a call without a token throws AuthTokenUnavailableError.
 * A missing token never turns demo data on.
 */
export function shouldUseDemoData(context?: DemoDataContext): boolean {
  const isProd = isProductionBuild();
  if (!isProd && context?.allowTestModeRequests === true) {
    return false;
  }
  if (!isProd && (runtimeEnv.isTestMode() || demoDataEnabled())) {
    return true;
  }
  requireToken(context);
  return false;
}

/** A missing token or a rejected one (401): the user is signed out. */
export function isSignedOutError(error: unknown): boolean {
  return (
    error instanceof AuthTokenUnavailableError ||
    (error instanceof ApiServiceError && error.status === 401)
  );
}
